import { BusinessDayCalculatorPL, ContextKeys, DocumentExchangeRateValidationBoPart, DocumentItemQuantityGreaterThanInventoryLevelError, DocumentItemQuantityGreaterThanInventoryLevelWarning, DocumentItemsNumberingBoPart, DocumentNumberBoPart, ExchangeRateMixin, InventoryKindQuery, InventoryQuery, LoggedUserQuery, NumberingQuery, ProductInventoryLevelQuery, PurchaseInvoiceAdjustmentDocumentRepositoryContract, rewriteDocumentContractor, rewriteDocumentItem, rewriteExchangeRate, UserCollectionQuery, VatRateForPurchaseQuery } from '@skylab-shared/business';
import { BO, BusinessObjectBase, QueryBus, ResponseConflictObject, Weaver } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryGoodsReceiptAdjustmentDocumentModel, InventoryGoodsReceiptDocumentModel, InventoryRelationshipCreationMethod, ProductModel, RelatedEntity } from '@skylab-shared/dataModel';
import { dateDiffInDays, head, not, substractDays } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { InventoryDocumentCalculatorBoPart } from './bo-parts/inventory-document-calculator.bo-part';
import { InventoryDocumentItemsValidationsBoPart } from './bo-parts/inventory-document-items-validations.bo-part';
import { InventoryDocumentErrorCodes, RequestedQuantityOfProductNotPresentInInventoryErrorObject } from './common/inventory-document-error-codes';
import { InventoryLinksMixin } from './mixins/inventory-links.mixin';
import { InventoryDocumentAssociatedWithGoodsIssueQuery } from './queries/inventory-document-associated-with-goods-issue.query';
import { refreshInventoryReceiptDocumentItemPriceAsync } from './utils/inventory-document-item-utils';
import { InventoryGoodsReceiptAdjustmentIssueDateEarlierThanAdjustedError } from './violations/inventory-goods-receipt-adjustment-issue-date-earlier-than-adjusted-error';
import { InventoryGoodsReceiptAdjustmentReceiptDateEarlierThanAdjustedError } from './violations/inventory-goods-receipt-adjustment-receipt-date-earlier-than-adjusted-error';
import { InventoryGoodsReceiptItemCannotBeOneTimeUsage } from './violations/inventory-goods-receipt-item-cannot-be-one-time-usage-error';

export interface InventoryGoodsReceiptAdjustmentDocumentBO extends InventoryLinksMixin<InventoryGoodsReceiptAdjustmentDocumentModel>, ExchangeRateMixin<InventoryGoodsReceiptAdjustmentDocumentModel> {};

@BO({
    modelType: InventoryGoodsReceiptAdjustmentDocumentModel,
    mixins: [InventoryLinksMixin, ExchangeRateMixin]
})
export class InventoryGoodsReceiptAdjustmentDocumentBO extends BusinessObjectBase<InventoryGoodsReceiptAdjustmentDocumentModel> {
    private itemsQuantityOnOriginalDocument: Map<InventoryDocumentItemModel, number>;

    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepository,
        protected readonly queryBus: QueryBus,
        protected readonly purchaseInvoiceAdjustmentRepository: PurchaseInvoiceAdjustmentDocumentRepositoryContract
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(InventoryQuery),
            this.localContext.preload(InventoryKindQuery),
            this.localContext.preload(LoggedUserQuery),
            this.localContext.preload(ContextKeys.company),
            this.localContext.preload(UserCollectionQuery)
        ]);

        if (this.model.isPersistent) {
            await this.loadLinksAsync();
            this.itemsQuantityOnOriginalDocument = new Map(this.model.items.map(item => [item, item.quantity]));
            this.internalIsDocumentAssociatedWithPurchaseDocument = !!this.linksCommercial;
        }

        await this.setDocumentAssociatedWithGoodsIssueAsync();

        this.registerDocumentReadonlyRules();
        this.registerDateValidationRules();
        this.registerItemRules();
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<InventoryDocumentErrorCodes, RequestedQuantityOfProductNotPresentInInventoryErrorObject>) {
        if ((conflict.code === InventoryDocumentErrorCodes.RequestedQuantityOfProductIsNotAvailable)) {
            const productIds = conflict.objectProperties?.filter((elem): elem is RequestedQuantityOfProductNotPresentInInventoryErrorObject => typeof elem !== 'string').map(elem => elem.productId);

            this.model.items.forEach(elem => {
                if (!elem.productId || !productIds?.includes(elem.productId)) return;
                this.addExternalRuleViolations<InventoryDocumentItemModel>({
                    [elem.sun]: [{
                        property: 'quantity',
                        violation: new DocumentItemQuantityGreaterThanInventoryLevelError()
                    }]
                });
                this.validation.queueValidationByViolationType(DocumentItemQuantityGreaterThanInventoryLevelWarning);
            });
        }
    }

    private registerDateValidationRules(): void {
        this.validation.addRule(
            [[InventoryGoodsReceiptAdjustmentDocumentModel, 'issueDate']],
            ctx => this.adjustedDocument?.issueDate && ctx.source.issueDate ? dateDiffInDays(ctx.source.issueDate, this.adjustedDocument.issueDate) >= 0 : true,
            InventoryGoodsReceiptAdjustmentIssueDateEarlierThanAdjustedError
        );

        this.validation.addRule(
            [[InventoryGoodsReceiptAdjustmentDocumentModel, 'receiptDate']],
            ctx => this.adjustedDocument ? dateDiffInDays(ctx.source.receiptDate, this.adjustedDocument.receiptDate) >= 0 : true,
            InventoryGoodsReceiptAdjustmentReceiptDateEarlierThanAdjustedError
        );
    }

    private registerDocumentReadonlyRules(): void {
        const modelType = InventoryGoodsReceiptAdjustmentDocumentModel;

        this.readonly.setAsReadonly([modelType, 'calculationMethod']);
        this.readonly.setAsReadonly([modelType, 'costCalculationMethod']);
        this.readonly.setAsReadonly([modelType, 'currencyId']);
        this.readonly.setAsReadonly([modelType, 'inventoryId']);
        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => true);

        this.readonly.addRule([[modelType, 'seller']], ctx => this.isDocumentAssociatedWithPurchaseDocument);
        this.readonly.addRule([
            [modelType, 'documentNumber'],
            [modelType, 'issueDate'],
            [modelType, 'issuer'],
            [modelType, 'numberingId'],
            [modelType, 'receiptDate'],
            [modelType, 'recipient'],
            [modelType, 'transactionLocation'],
        ], () => this.isDocumentAssociatedWithGoodsIssue || this.isDocumentAdjusted || this.isDocumentAssociatedWithPurchaseDocument);
    }

    private registerItemRules(): void {
        this.validation.addRule(
            [[InventoryDocumentItemModel, 'productName'], [InventoryDocumentItemModel, 'productId']],
            [[InventoryDocumentItemModel, 'productName']],
            ctx => not(!ctx.source.productId && !!ctx.target.productName),
            InventoryGoodsReceiptItemCannotBeOneTimeUsage
        );

        this.readonly.addRule(
            [[InventoryDocumentItemModel, 'productId']],
            [[InventoryDocumentItemModel, 'productName'], [InventoryDocumentItemModel, 'unitId']],
            ctx => not(!ctx.source.productId)
        );

        this.readonly.setReadonlyOnSubtree(InventoryDocumentItemModel, () => this.isDocumentAssociatedWithGoodsIssue || this.isDocumentAdjusted || this.isDocumentAssociatedWithPurchaseDocument);

        this.validation.addRule(
            [[InventoryDocumentItemModel, 'quantity']],
            async ctx => {
                if (ctx.source.quantity < 0) return true;

                if (this.validationResult.find(elem => (elem.ruleViolation.violation instanceof DocumentItemQuantityGreaterThanInventoryLevelError)
                    && elem.source === ctx.source)) return true;

                const validate = async (previousQuantity: number) => {
                    if (ctx.source.quantity >= previousQuantity) return true;

                    const inventoryLevel = await this.getProductInventoryLevel(ctx.source);
                    if (inventoryLevel === null) return true;

                    const inventoryLevelAfterAdjustment = (inventoryLevel - (previousQuantity - ctx.source.quantity));
                    return inventoryLevelAfterAdjustment >= 0;
                };

                const adjustedItem = this.adjustedDocument?.items.find(elem => elem.ordinalNumber === ctx.source.ordinalNumber);
                const editedItemQuantity = this.itemsQuantityOnOriginalDocument?.get(ctx.source);

                if (!this.model.isPersistent && adjustedItem)
                    return await validate(adjustedItem.quantity);

                if (this.model.isPersistent && editedItemQuantity)
                    return await validate(editedItemQuantity);

                return true;
            },
            DocumentItemQuantityGreaterThanInventoryLevelWarning
        );
    }

    private async getProductInventoryLevel(item: InventoryDocumentItemModel): Promise<number | null> {
        const productInventoryData = await this.localContext.getAsync(ContextKeys.productInventoryLevel(item.productId!), () => this.queryBus.queryAsync(new ProductInventoryLevelQuery(item.productId!)));
        if (!productInventoryData) return null;

        return productInventoryData.quantity;
    }

    private initBOParts(): void {
        this.parts.push(...[
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new InventoryDocumentCalculatorBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new InventoryDocumentItemsValidationsBoPart(this.weaver),
            new DocumentExchangeRateValidationBoPart(this.weaver)
        ]);
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.receiptDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => {
            const product = item.productId ? await this.localContext.getAsync(ContextKeys.product(item.productId), () => this.productRepository.findAsync(item.productId!)) : new ProductModel();
            await refreshInventoryReceiptDocumentItemPriceAsync(item, this.localContext, this.model, product);
        }));
    }

    private _adjustedDocument: InventoryGoodsReceiptDocumentModel | InventoryGoodsReceiptAdjustmentDocumentModel;
    public set adjustedDocument(document: InventoryGoodsReceiptDocumentModel | InventoryGoodsReceiptAdjustmentDocumentModel) {
        this._adjustedDocument = document;
    }

    public get adjustedDocument() {
        return this._adjustedDocument;
    }

    public isDocumentAssociatedWithGoodsIssue: boolean;
    private async setDocumentAssociatedWithGoodsIssueAsync(): Promise<void> {
        this.isDocumentAssociatedWithGoodsIssue = this.model.isPersistent
            ? await this.queryBus.queryAsync(new InventoryDocumentAssociatedWithGoodsIssueQuery(this.model.id))
            : false;
    }

    private internalIsDocumentAssociatedWithPurchaseDocument: boolean;
    public get isDocumentAssociatedWithPurchaseDocument() {
        return this.internalIsDocumentAssociatedWithPurchaseDocument;
    };

    public async fillGoodsReceiptAdjustmentToPurchaseDocumentAdjustmentAsync(purchaseDocumentId: number, creationMethod: InventoryRelationshipCreationMethod): Promise<void> {
        this.internalIsDocumentAssociatedWithPurchaseDocument = true;

        this.model.adjustedDocumentId = this.adjustedDocument.id;
        this.model.adjustedDocumentVersion = this.adjustedDocument.version;

        const numberings = this.localContext.getPreloaded(NumberingQuery)!;
        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.indexOf(this.model.kind) > -1);

        const inventory = head(this.localContext.getPreloaded(InventoryQuery)!);

        const purchaseDocument = await this.purchaseInvoiceAdjustmentRepository.findAsync(purchaseDocumentId);

        this.model.numberingId = defaultNumberingId;
        this.model.issuedByVatPayer = purchaseDocument.issuedByVatPayer;
        this.model.calculationMethod = purchaseDocument.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net;
        this.model.costCalculationMethod = purchaseDocument.costCalculationMethod;
        this.model.transactionLocation = purchaseDocument.transactionLocation;
        this.model.recipient = purchaseDocument.recipient;
        this.model.issuer = purchaseDocument.issuer;

        this.model.issueDate = purchaseDocument.issueDate;
        this.model.receiptDate = purchaseDocument.deliveryDate;
        this.model.currencyId = purchaseDocument.currencyId;

        this.model.creationMethod = creationMethod;

        if (purchaseDocument.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, purchaseDocument.exchangeRate);
        }

        rewriteDocumentContractor(this.model, purchaseDocument);

        if (inventory) {
            this.model.inventoryId = inventory.id;
        }

        purchaseDocument.items.filter(elem => elem.includesInventoryControl).sort((i1, i2) => i1.ordinalNumber - i2.ordinalNumber).forEach(item => {
            const model = new InventoryDocumentItemModel();
            this.model.items.push(model);
            rewriteDocumentItem(model, item);
            model.relatedCommercialItemsIds = item.id ? [item.id] : [];
            model.note = null;
            return model;
        });

        this.model.items.forEach(item => {
            const adjustedItem = this.adjustedDocument.items.find(i => i.ordinalNumber === item.ordinalNumber);
            if (adjustedItem) {
                item.adjustedItemId = adjustedItem.id || null;
            }
        });

        this.model.relatedCommercialDocuments = [RelatedEntity.from(purchaseDocument)];
    }
}
